import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { ConnectTargetsPanel } from '@/staff/ConnectTargetsPanel';
import { StudyHelperInfo } from '@/tables/StudyHelperInfo';
import {
  type StudyHelperHistoryRecord,
  StudyHelpersHistoryTable,
} from '@/tables/StudyHelpersHistoryTable';
import { GroupHeaderWrapper } from '~/styles/layoutElements';
import { apiLanguageToLocale } from '~/utils/languageFormat';

export const Route = createFileRoute(
  '/_app/_users/members/$memberId/study-helpers',
)({
  component: MemberStudyHelpersRoute,
});

export default function MemberStudyHelpersRoute() {
  const { t } = useTranslation();
  const { memberId } = Route.useParams();
  const studentId = Number(memberId);

  // Legacy refuses this tab by not registering it, so a denied viewer gets a
  // 404 page, not a permission message. `viewer-access` answers 200 for every
  // signed-in caller and gates on the same audience as the two study-helper
  // operations, so one query decides the page.
  //
  // No `retry: false`: the gate is positive, so a retried failure holds the
  // page at `null` and can't leak content.
  const { data: access, isLoading } = api.getStudentViewerAccess.useQuery({
    path: { student_id: studentId },
  });
  const { data: history } = api.getStudentStudyHelperHistory.useQuery({
    path: { student_id: studentId },
  });

  if (isLoading) {
    // Not a no-op: rendering the headings before `access` resolves would flash
    // them at a viewer the next line refuses.
    return null;
  }

  // Positive test: `access` is undefined on a 401, a 500 or a dropped
  // connection, and each of those is a refusal. `can_view === false` would
  // render the page, whose failing queries would then say the student has no
  // study helper.
  if (access?.can_view !== true) {
    return <NotFoundScreen />;
  }

  // The caption comes from `viewer-access`, not `getStudent`, which is
  // admin-or-self and returns far more than a table title needs.
  const studentName = access.display_name ?? '';
  const historyRecords: StudyHelperHistoryRecord[] = (history ?? []).map(
    (row) => ({
      id: String(row.id),
      studyHelper: row.study_helper_name,
      location: row.location,
      languages: row.languages.map(apiLanguageToLocale),
      adoptedAt: row.adopted_at,
      releasedAt: row.released_at,
      releasedBy: row.released_by,
    }),
  );

  return (
    <MainContent>
      {/* Both headings and the history table render with no helper
         assigned; only the helper's details and the Message button depend
         on one, as in legacy. */}
      <GroupHeaderWrapper>
        <TitleWithQuestion
          title={t('lessons.current-study-helper')}
          placement="study_helpers"
        />
      </GroupHeaderWrapper>
      <StudyHelperInfo studentId={studentId} />
      <GroupHeaderWrapper>
        <TitleWithQuestion
          title={t('lessons.study-helper-history')}
          placement="study_helpers"
        />
      </GroupHeaderWrapper>
      <StudyHelpersHistoryTable
        studentName={studentName}
        data={historyRecords}
      />
      {/* Renders nothing for a non-staff viewer, and never sends the
         AdminCheck read for one. */}
      <ConnectTargetsPanel studentId={studentId} />
    </MainContent>
  );
}

const MainContent = styled.main`
  background: var(--color-background);
  flex: 1;
  margin: 34px 0 30px 0;
  min-width: 0;
`;
